/**
 * `window.tjPasskeyDebug("you@example.com")` — one-shot diagnosis of the passkey login path.
 *
 * A restricted ceremony can only answer "did it work"; when the browser jumps to the cross-device
 * QR there is no error at all, because from its point of view nothing matched. This asks the two
 * questions that separate the possible causes:
 *
 * 1. **What does the server think this account owns?** — the device envelopes, i.e. the credential
 *    ids the login will put in `allowCredentials`. A missing `device_envelopes` field also proves
 *    an older worker is still deployed.
 * 2. **What does this browser actually hold?** — a *discoverable* request (empty `allowCredentials`)
 *    lets the authenticator answer with any passkey it has for the rpId, without the account's
 *    records being involved. Comparing its id with (1) says whether the account is stale or the
 *    client is asking for the wrong thing.
 *
 * The probe does not sign anyone in: no token is stored, no envelope is opened, no PRF is derived.
 */

import {
  abortPendingCeremony,
  bytesToBase64,
  resolveEffectiveRpId,
  type WrappedDeviceMasterKey,
} from "@tensilelabs/auth-client";
import { lookupAccount } from "../api";
import {
  authDebug,
  describeClientCapabilities,
  describeEnvelope,
  installAuthTraceHelpers,
} from "./debug";

export interface PasskeyProbeResult {
  /** Device records the account has, as the login response reports them. */
  envelopes: Array<Pick<WrappedDeviceMasterKey, "device_id" | "credential_id_b64">>;
  /** True when the worker returned the multi-device field (i.e. the passkey fix is deployed). */
  workerReturnsDeviceEnvelopes: boolean;
  /** Credential id this browser answered a discoverable request with, if any. */
  localCredentialIdB64: string | null;
  /** The envelope that credential belongs to, if the account knows about it. */
  matchingDeviceId: string | null;
  /** Human-readable conclusion, also printed to the console. */
  verdict: string;
}

export async function diagnosePasskey(emailInput: string): Promise<PasskeyProbeResult> {
  const email = emailInput.trim().toLowerCase();
  const rpId = resolveEffectiveRpId("tensilelabs.com");

  authDebug("probe:start", { email, rpId });
  authDebug("probe:capabilities", await describeClientCapabilities());

  const lookup = await lookupAccount(email);
  const envelopes = lookup.device_envelopes ?? [];
  const workerReturnsDeviceEnvelopes = Array.isArray(lookup.device_envelopes);

  authDebug("probe:login-response", {
    workerReturnsDeviceEnvelopes,
    envelopeCount: envelopes.length,
    keys: Object.keys(lookup),
  });
  envelopes.forEach((envelope, index) => {
    authDebug(`probe:envelope[${index}]`, describeEnvelope(envelope));
  });

  let localCredentialIdB64: string | null = null;
  let matchingDeviceId: string | null = null;
  let verdict: string;

  if (!navigator.credentials?.get || typeof window.PublicKeyCredential === "undefined") {
    verdict =
      "This browser exposes no WebAuthn API, so the SDK falls back to a software passkey and no " +
      "prompt can appear. The login cannot work in this browser.";
    authDebug("probe:verdict", { verdict });
    return {
      envelopes: envelopes.map(({ device_id, credential_id_b64 }) => ({
        device_id,
        credential_id_b64,
      })),
      workerReturnsDeviceEnvelopes,
      localCredentialIdB64,
      matchingDeviceId,
      verdict,
    };
  }

  // The failed sign-in attempt may still hold the document's single ceremony slot; the browser
  // reports that as `OperationError: A request is already pending`, which reads like a dismissal.
  // Clear it and give the prompt a moment to tear down before asking again.
  if (abortPendingCeremony()) await sleep(300);

  // Discoverable probe: no allowCredentials, no PRF. The authenticator answers with whatever
  // passkey it holds for this rpId, which is precisely what the restricted login cannot reveal.
  //
  // `hints: ["client-device"]` is what makes the answer legible: without it the client escalates to
  // the cross-device hybrid flow (the QR code) whenever nothing matches locally, so "no passkey
  // here" and "a prompt is waiting for your phone" look identical. Restricted to the local device,
  // a browser holding no credential for this rpId is rejected in milliseconds and no QR appears.
  const startedAt = Date.now();
  try {
    const assertion = await discoverableAssertion(rpId, ["client-device"]);

    if (assertion) {
      localCredentialIdB64 = bytesToBase64(new Uint8Array(assertion.rawId));
      matchingDeviceId =
        envelopes.find((envelope) => envelope.credential_id_b64 === localCredentialIdB64)
          ?.device_id ?? null;
      authDebug("probe:discoverable-credential", {
        localCredentialIdB64,
        registeredToAccount: Boolean(matchingDeviceId),
        matchingDeviceId,
      });

      verdict = matchingDeviceId
        ? `This browser's passkey IS registered to the account (device ${matchingDeviceId}). The ` +
          "restricted login should therefore have prompted locally — send this whole log."
        : `This browser holds a passkey for rpId "${rpId}" that the account has NO device record ` +
          `for (credential ${localCredentialIdB64}). The account's device list is stale: signing ` +
          "in from this browser cannot unwrap the vault until this credential is registered as a " +
          "device.";
    } else {
      verdict = "The browser returned no credential for a discoverable request.";
    }
  } catch (cause) {
    const name = cause instanceof DOMException ? cause.name : "";
    const detail = cause instanceof Error ? cause.message : String(cause);
    const elapsedMs = Date.now() - startedAt;
    authDebug("probe:discoverable-error", { name, message: detail, elapsedMs });

    if (/already pending/i.test(detail)) {
      verdict =
        "Another passkey prompt is still open (" +
        detail +
        "). Cancel it — the QR sheet counts — and click again.";
    } else if (name === "NotAllowedError" && elapsedMs < 1500) {
      // Rejected without ever showing a prompt: the client had nothing to offer for this rpId.
      verdict =
        `This device holds NO passkey for rpId "${rpId}" (rejected in ${elapsedMs}ms without a ` +
        "prompt). The passkey in your password manager belongs to a different site — most likely " +
        "`localhost` or a preview host it was created on — so it can never satisfy this login. " +
        "Register a passkey on this domain to sign in here.";
    } else if (name === "NotAllowedError") {
      verdict =
        `A passkey prompt was shown for rpId "${rpId}" and dismissed after ${elapsedMs}ms, so this ` +
        "device probably does hold a usable passkey. Click again and approve the prompt.";
    } else {
      verdict = `The discoverable probe failed: ${detail}`;
    }
  }

  authDebug("probe:verdict", { verdict });
  console.warn("[taskjinn-auth] verdict:", verdict);
  return {
    envelopes: envelopes.map(({ device_id, credential_id_b64 }) => ({
      device_id,
      credential_id_b64,
    })),
    workerReturnsDeviceEnvelopes,
    localCredentialIdB64,
    matchingDeviceId,
    verdict,
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * One discoverable assertion, retried past the document's single ceremony slot.
 *
 * Chrome can still answer `A request is already pending` for a moment after the previous request
 * settles, so a single attempt turns a stale prompt into a wrong verdict.
 */
async function discoverableAssertion(
  rpId: string,
  hints: string[],
  attempts = 3,
): Promise<PublicKeyCredential | null> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return (await navigator.credentials.get({
        publicKey: {
          rpId,
          challenge: crypto.getRandomValues(new Uint8Array(32)),
          allowCredentials: [],
          userVerification: "discouraged",
          timeout: 60000,
          // WebAuthn L3 hints; `client-device` keeps the request on this machine's authenticator.
          // Not in every TypeScript DOM lib yet, hence the cast.
          hints,
        } as unknown as PublicKeyCredentialRequestOptions,
      })) as PublicKeyCredential | null;
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause);
      if (attempt === attempts - 1 || !/already pending/i.test(message)) throw cause;
      authDebug("probe:retry-after-pending-request", { attempt: attempt + 1, message });
      await sleep(500 * (attempt + 1));
    }
  }
  return null;
}

/** Exposes the probe and the trace helpers on `window`. */
export function installPasskeyDebug(): void {
  (window as unknown as { tjPasskeyDebug?: typeof diagnosePasskey }).tjPasskeyDebug =
    diagnosePasskey;
  installAuthTraceHelpers();
  authDebug("debug-helper-installed", {
    probe: "window.tjPasskeyDebug('you@example.com')",
    trace: "window.tjAuthTrace.download()",
    console: "add ?debug=1 to the URL",
  });
}
